import React, { useEffect, useRef, useState } from 'react';
import { get } from 'lodash';

import style from './style.module.css';
import Link from '../../../Link';
import Spinner from '../../../Spinner';
import AsyncGet from '../../../Materials/AsyncGet';
import FormGroup from '../../FormGroup';
import { getIcon } from '../../../../helpers/icons';
import useOutsideClick from '../../../../hooks/useOutSideClick';

const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 400;

const Searchable = (props) => {
    const {
        meta,
        input: { value, onChange, onBlur, name },
        label = false,
        required = false,
        disabled = false,
        prompt = "None",
        placeholder = "Type to search...",
        async = {},
        onSelect = () => { },
    } = props;

    const [selectedItem, setSelectedItem] = useState(null);
    const [keyword, setKeyword] = useState("");
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [load, setLoad] = useState(false);
    const [resolveLoad, setResolveLoad] = useState(false);
    const divRef = useRef(null);
    const pagingRef = useRef(false);

    useOutsideClick(divRef, () => { closeList() });

    // Resolve display value from items list (like Dropdown's selectValue)
    const selectValue = (list = []) => {
        if (value === "") {
            return prompt;
        }
        const item = list.find(d => get(d, 'id') === value);
        if (item) {
            return get(item, 'name');
        }
        if (selectedItem && selectedItem.id === value) {
            return selectedItem.name;
        }
        return prompt;
    };

    // Resolve the saved selection by its id so the name shows even when the
    // option is beyond the first fetched page (e.g. after a page refresh).
    useEffect(() => {
        if (value && get(selectedItem, 'id') !== value) {
            setResolveLoad(Date.now());
        }
        // eslint-disable-next-line
    }, [value]);

    // Debounced server-side search while the list is open
    useEffect(() => {
        if (open === false) {
            return;
        }
        const timer = setTimeout(() => {
            fetchPage(0);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line
    }, [keyword]);

    const fetchPage = (newOffset) => {
        pagingRef.current = true;
        if (newOffset === 0) {
            setItems([]);
        }
        setOffset(newOffset);
        setLoad(Date.now());
    }

    const openList = () => {
        if (open === true || disabled === true) {
            return;
        }
        setOpen(true);
        setKeyword("");
        fetchPage(0);
    }

    const closeList = () => {
        setOpen(false);
        setKeyword("");
    }

    const selectItem = (item) => {
        setSelectedItem(item);
        onChange(get(item, 'id', ''));
        onSelect(item);
        closeList();
        onBlur();
    }

    const onScrollList = (event, fetching) => {
        const el = event.target;
        const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 16;
        if (nearBottom && fetching === false && pagingRef.current === false && items.length < total) {
            fetchPage(items.length);
        }
    }

    return (
        <div className={style.finder} ref={divRef}>
            <FormGroup
                meta={meta}
                label={label}
                value={selectValue(items)}
                required={required}
                disabled={disabled}
                fieldName={name}
                fieldValue={value}
            >
                <div className={style.wrap}>
                    <input
                        placeholder={selectValue(items) || placeholder}
                        value={open ? keyword : selectValue(items)}
                        onFocus={openList}
                        onClick={openList}
                        onChange={(e) => setKeyword(e.target.value)}
                        onBlur={() => onBlur()}
                    />
                    <span className={`${style.findIcon}${open ? ` ${style.searchIcon}` : ` ${style.triangeIcon}`}`}>
                        <i className={getIcon(open ? 'Search' : 'Triange')}></i>
                    </span>
                    <AsyncGet
                        {...async}
                        loadOnMount={resolveLoad}
                        paramsData={{ id: value }}
                        permissionText={false}
                        onFetch={({ data }) => {
                            const item = get(data, 'list.0', null);
                            if (item) {
                                setSelectedItem(item);
                            }
                        }}
                    />
                    <AsyncGet
                        {...async}
                        loadOnMount={load}
                        paramsData={{ keyword, limit: PAGE_SIZE, offset }}
                        permissionText={false}
                        onFetch={({ data }) => {
                            pagingRef.current = false;
                            const list = get(data, 'list', []);
                            setItems((prev) => (offset === 0 ? list : [...prev, ...list]));
                            if (offset === 0) {
                                setTotal(get(data, 'total', 0));
                            }
                        }}
                        onError={() => {
                            pagingRef.current = false;
                        }}
                        render={({ fetching }) => (
                            <>
                                {open && (
                                    <div className={style.dropdownOptions} onScroll={(event) => onScrollList(event, fetching)}>
                                        {!disabled && (
                                            <Link className={style.item} onClick={() => selectItem({ id: '', name: '' })}>
                                                <span>{prompt}</span>
                                            </Link>
                                        )}
                                        {items.map((item) => (
                                            <Link
                                                className={`${style.item}${get(item, 'id') === value ? ` ${style.active}` : ''}`}
                                                key={get(item, 'id')}
                                                onClick={() => selectItem(item)}
                                            >
                                                <span>{get(item, 'name')}</span>
                                            </Link>
                                        ))}
                                        {fetching === true && <div className={`${style.item} ${style.loading}`}><Spinner /></div>}
                                        {fetching === false && items.length === 0 && (
                                            <div className={style.item}>No results were found</div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    />
                </div>
            </FormGroup>
        </div>
    )
}

export default Searchable;